import { useState, useContext } from "react"

//Formik
import { Form, Formik, useField } from "formik"
import * as Yup from "yup"
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikTextAreaInput from "../../../helpers/formikInputs/FormikTextAreaInput"
import FormikCheckboxInput from "../../../helpers/formikInputs/FormikCheckboxInput"
import FormikMultiSelectInput from "../../../helpers/formikInputs/FormikMultiSelectInput"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"

//PrimeReact
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { InputTextarea } from "primereact/inputtextarea"
import { MultiSelect } from "primereact/multiselect"

//Notification components
import { GET_ORGANIZATIONS } from "../notificationsQueries"
import { noteApiCreateNote, noteApiSendToast } from "../notificationsApi"

//styles
import styles from "../../../styles/Form-Inputs.module.css"

// Context
import { UserContext } from "../../../context/userContext"
import { Checkbox } from "primereact/checkbox"
import { useLazyQuery } from "@apollo/client"

export default function NotificationCreateSystemForm(props) {
  const userCtx = useContext(UserContext)

  const [enterprises, setEnterprises] = useState([])

  const [loadedOrganizations, setLoadedOrganizations] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [filteredOrganizations, setFilteredOrganizations] = useState([])

  const defaultFormValues = {
    title: "",
    description: "",
    eventType: "systemNotification",
    highPriority: false,
    systemNotification: true,
    showToast: true,
    enterpriseId: [],
    groupId: [],
    userId: "0",
    systemNotificationFilter: "Staff",
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Please enter a title"),
  })

  const [getOrgs] = useLazyQuery(GET_ORGANIZATIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
    },
  })

  if (!loadedOrganizations) {
    setLoadedOrganizations(true)
    getOrgs()
      .then((result) => {
        const orgs = [
          ...result.data.organizationsByUid.map((org) => {
            return {
              enterprise_id: org.enterprise_id,
              organization_id: org.organization_id,
              organization_name: org.organization_name,
            }
          }),
        ]

        setOrganizations(orgs)
        setFilteredOrganizations(orgs)

        let enterpriseIds = []
        let enterprises = []
        result.data.organizationsByUid.forEach((org) => {
          if (!enterpriseIds.some((id) => id === org.enterprise_id)) {
            enterpriseIds.push(org.enterprise_id)
            enterprises.push({
              enterprise_id: org.enterprise_id,
              enterprise_name: org.enterprise_name,
            })
          }
        })

        setEnterprises(enterprises)
      })
      .catch((error) => {
        console.error(`Unable to retrieve organizations`, error)
        props.onShowError(
          `Unable to retrieve organizations. See console for details.`
        )
      })
  }

  function GetTextBox({ fieldName, displayName, multiLine }) {
    const [field, meta] = useField(fieldName)

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: "bold" }}>{displayName}</div>
        <div>
          {multiLine ? (
            <InputTextarea style={{ width: "100%" }} {...field} rows={5} />
          ) : (
            <InputText style={{ width: "100%" }} {...field} />
          )}
        </div>
        <div>
          {meta.touched && meta.error ? (
            <label id={`title-error`} className={styles.errFieldMsg}>
              {meta.error}
            </label>
          ) : null}
        </div>
      </div>
    )
  }

  function GetEnterpriseDropdown({ isSubmitting }) {
    const [field, meta, helpers] = useField("enterpriseId")
    const [groupField, , groupHelpers] = useField("groupId")

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: "bold" }}>Enterprise</div>
        <div>
          <MultiSelect
            style={{ width: "100%", marginBottom: "5px" }}
            options={enterprises}
            placeholder="All enterprises"
            optionLabel="enterprise_name"
            value={field.value}
            onChange={(value) => {
              helpers.setValue(value.value)

              setFilteredOrganizations(
                organizations.filter(
                  (org) =>
                    value.value.some(
                      (ent) => ent.enterprise_id === org.enterprise_id
                    ) || value.value.length === 0
                )
              )

              if (value.value !== "0" && groupField.value !== "0") {
                const org = organizations.filter(
                  (org) => org.organization_id === groupField.value
                )[0]
                if (org.enterprise_id !== value.value) {
                  groupHelpers.setValue("0")
                }
              }
            }}
            disabled={isSubmitting || enterprises.length < 1}
            filter
          />
        </div>
        <div>
          {meta.touched && meta.error ? (
            <label id={`title-error`} className={styles.errFieldMsg}>
              {meta.error}
            </label>
          ) : null}
        </div>
      </div>
    )
  }

  function GetOrganizationDropdown({ isSubmitting }) {
    const [field, meta, helpers] = useField("groupId")

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: "bold" }}>Organization</div>
        <div>
          <MultiSelect
            style={{ width: "100%", marginBottom: "5px" }}
            options={filteredOrganizations}
            placeholder="All organizations"
            optionLabel="organization_name"
            value={field.value}
            onChange={(value) => {
              helpers.setValue(value.value)
            }}
            disabled={isSubmitting || organizations.length < 1}
            filter
          />
        </div>
        <div>
          {meta.touched && meta.error ? (
            <label id={`title-error`} className={styles.errFieldMsg}>
              {meta.error}
            </label>
          ) : null}
        </div>
      </div>
    )
  }

  function GetUserDropdown({ isSubmitting }) {
    const [field, meta, helpers] = useField("systemNotificationFilter")

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: "bold" }}>Recipients</div>
        <div>
          <Dropdown
            style={{ width: "100%", marginBottom: "5px" }}
            options={["All", "Staff", "Participants"]}
            placeholder="Select an option"
            value={field.value}
            onChange={(value) => {
              helpers.setValue(value.value)
            }}
            disabled={isSubmitting}
            filter
          />
        </div>
        <div>
          {meta.touched && meta.error ? (
            <label id={`title-error`} className={styles.errFieldMsg}>
              {meta.error}
            </label>
          ) : null}
        </div>
      </div>
    )
  }

  function GetCheckBox({ fieldName, titleValue, disabled }) {
    const [field, meta, helpers] = useField(fieldName)

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: "bold" }}>{titleValue}</div>
        <div>
          <Checkbox
            inputId={fieldName}
            checked={field.value}
            onChange={(e) => {
              helpers.setValue(e.checked)
            }}
            {...field}
            disabled={disabled}
          />
        </div>
        <div>
          {meta.touched && meta.error ? (
            <label id={`title-error`} className={styles.errFieldMsg}>
              {meta.error}
            </label>
          ) : null}
        </div>
      </div>
    )
  }

  async function createNotification(values) {
    return noteApiCreateNote(userCtx.tokenNotification, values)
      .then((result) => {
        if (values.showToast) {
          noteApiSendToast(userCtx.tokenNotification, result.id).catch(
            (error) => {
              console.error("Unable to list system notifications", error)
              props.onShowError("Unable to send notification toasts")
            }
          )
        }
        props.onRefreshNotifications()
        return true
      })
      .catch((error) => {
        console.error("Unable to create system notifications", error)
        props.onShowError(
          "Unable to create system notifications. See console for details."
        )
        return false
      })
  }

  async function SendEnterpriseWideNotification(values, enterprise) {
    return await createNotification({
      ...values,
      enterpriseId: enterprise.enterprise_id,
      groupId: "0",
    })
  }

  async function SendGroupWideNotification(values, organization) {
    return await createNotification({
      ...values,
      enterpriseId: organization.enterprise_id,
      groupId: organization.organization_id,
    })
  }

  async function saveChanges(values) {
    let wasOk = true
    let notifications = []

    if (
      values.enterpriseId.length === 0 &&
      values.groupId.length === 0 &&
      userCtx.session.user.staff_role <= 2 //This is enterprise admins and higher
    ) {
      //Send per enterprise
      enterprises.forEach((ent) => {
        notifications.push(SendEnterpriseWideNotification(values, ent))
      })
    } else if (
      values.groupId.length === 0 &&
      userCtx.session.user.staff_role > 2
    ) {
      //This is group admins and lower
      //Send per filtered group
      filteredOrganizations.forEach((org) => {
        notifications.push(SendGroupWideNotification(values, org))
      })
    } else {
      //Send per selected group
      values.groupId.forEach((org) => {
        notifications.push(SendGroupWideNotification(values, org))
      })

      //Check for selected enterprises with no group selection, these are enterprise wide broadcasts
      let orphanEnterprises = values.enterpriseId.filter(
        (ent) =>
          !values.groupId.some((grp) => grp.enterprise_id === ent.enterprise_id)
      )
      if (orphanEnterprises.length > 0) {
        if (userCtx.session.user.staff_role <= 2) {
          //This is enterprise admins and higher
          orphanEnterprises.forEach((ent) =>
            notifications.push(SendEnterpriseWideNotification(values, ent))
          )
        } else {
          //Send for all groups in enterprise
          filteredOrganizations
            .filter((org) =>
              orphanEnterprises.some(
                (ent) => ent.enterprise_id === org.enterprise_id
              )
            )
            .forEach((org) =>
              notifications.push(SendGroupWideNotification(values, org))
            )
        }
      }
    }

    await Promise.all(notifications).then((result) => {
      wasOk = !result.some((x) => !x)
    })

    if (wasOk) {
      props.onShowNotification("System notification created successfully")
      props.onSetShowForm(false)
    }

    return wasOk
  }

  return (
    <div style={{ width: "100%" }}>
      <Formik
        initialValues={defaultFormValues}
        enableReinitialize
        validateOnChange={true}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          saveChanges(values)
        }}
      >
        {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
          <Form style={{ width: "100%" }}>
            <div>
              <FormikTextInput
                name={"title"}
                label={"Title"}
                disabled={isSubmitting}
              />
              <FormikCheckboxInput
                name={"highPriority"}
                label={"High Priority"}
                disabled={isSubmitting}
              />

              <FormikCheckboxInput
                name={"showToast"}
                label={"Show Toast"}
                disabled={isSubmitting}
              />

              <FormikMultiSelectInput
                name={"enterpriseId"}
                label={"Enterprise"}
                options={enterprises}
                optionLabel={"enterprise_name"}
                placeHolderValue={"All enterprises"}
                onChangeCallBack={(newValue) => {
                  setFilteredOrganizations(
                    organizations.filter(
                      (org) =>
                        newValue.some(
                          (ent) => ent.enterprise_id === org.enterprise_id
                        ) || newValue.length === 0
                    )
                  )

                  if (newValue.length !== 0 && values?.groupId !== "0") {
                    const org = organizations.filter(
                      (org) => org.organization_id === values?.groupId
                    )[0]
                    if (org.enterprise_id !== newValue) {
                      setFieldValue("groupId", "0")
                    }
                  }
                }}
                disabled={isSubmitting || enterprises.length < 1}
              />
              <FormikMultiSelectInput
                name={"groupId"}
                label={"Organization"}
                placeHolderValue={"All organizations"}
                options={filteredOrganizations}
                optionLabel={"organization_name"}
                disabled={isSubmitting || organizations.length < 1}
              />
              <FormikDropDown
                name={"systemNotificationFilter"}
                label={"Recipients"}
                placeHolderValue={"Select an option"}
                options={["All", "Staff", "Participants"]}
                disabled={isSubmitting}
                filter={true}
              />
              <FormikTextAreaInput
                name={"description"}
                label={"Description"}
                disabled={isSubmitting}
                rows={5}
              />
              <div>
                <button
                  type="button"
                  className="btn"
                  style={{ float: "right" }}
                  onClick={() => {
                    props.onSetShowForm(false)
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{ float: "right", marginRight: 10 }}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  Save
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
